import React from "react";
import { ArrowRight, ChevronDown } from "lucide-react";
import { Button } from "./ui/button";
import { heroContent, images } from "../data/mock";

export const HeroSection = () => {
  const scrollToSection = (id) => {
    document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        <img
          src={images.hero}
          alt="Wedding dance floor atmosphere"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-charcoal-dark/90 via-charcoal-dark/75 to-charcoal-dark/50" />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal-dark/60 via-transparent to-charcoal-dark/30" />
      </div>

      {/* Grain overlay */}
      <div className="absolute inset-0 grain-overlay" />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 pt-32 pb-20 w-full">
        <div className="max-w-3xl">
          {/* Badge */}
          <div
            className="animate-fade-in-up inline-flex items-center gap-2 px-4 py-2 rounded-full border border-gold/30 bg-gold/10 mb-8"
            style={{ backdropFilter: "blur(8px)" }}
          >
            <span className="w-2 h-2 rounded-full bg-gold animate-pulse" />
            <span className="text-gold text-sm font-medium tracking-wide">
              Premium Wedding DJ Service
            </span>
          </div>

          {/* Headline */}
          <h1
            className="animate-fade-in-up font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.1] mb-6"
            style={{ animationDelay: "100ms" }}
          >
            {heroContent.headline}
          </h1>

          {/* Subheadline */}
          <p
            className="animate-fade-in-up text-lg sm:text-xl text-white/70 leading-relaxed max-w-2xl mb-10 font-body"
            style={{ animationDelay: "200ms" }}
          >
            {heroContent.subheadline}
          </p>

          {/* Urgency line */}
          <p
            className="animate-fade-in-up text-sm text-gold/80 font-medium tracking-wide mb-4"
            style={{ animationDelay: "280ms" }}
          >
            2026 wedding dates are already booking. Secure yours early.
          </p>

          {/* CTAs */}
          <div
            className="animate-fade-in-up flex flex-col sm:flex-row gap-4 mb-16"
            style={{ animationDelay: "300ms" }}
          >
            <Button
              onClick={() => scrollToSection("#contact")}
              className="bg-gold hover:bg-gold-dark text-white font-semibold px-8 h-14 text-base rounded-full shadow-lg shadow-gold/20"
            >
              {heroContent.primaryCTA}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button
              onClick={() => scrollToSection("#contact")}
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10 hover:text-white font-semibold px-8 h-14 text-base rounded-full"
              style={{ backdropFilter: "blur(4px)" }}
            >
              {heroContent.secondaryCTA}
            </Button>
          </div>

          {/* Stats */}
          <div
            className="animate-fade-in-up flex gap-12"
            style={{ animationDelay: "400ms" }}
          >
            {heroContent.stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="font-display text-3xl sm:text-4xl font-bold text-gold">
                  {stat.number}
                </div>
                <div className="text-sm text-white/50 mt-1 tracking-wide uppercase">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <button
        onClick={() => scrollToSection("#value-proposition")}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-white/50 hover:text-gold animate-bounce-slow"
        aria-label="Scroll down"
      >
        <ChevronDown className="h-8 w-8" />
      </button>
    </section>
  );
};
